import { useCallback, useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { updateWidgetData as updateWidgetDataAction, addWidgetSection, deleteWidgetSection, updateSiteContentRequest, selectPlainSiteContent } from '../redux/slices/siteContentSlice';
import { toggleEditMode } from '../redux/slices/editModeSlice';
import { Widget } from '../utils/widgetUtils';
import { useSelector } from 'react-redux';
import { WidgetType } from '../widgets/widgetRegistry';
import { generatePageWidgetId, isPageWidget } from '../utils/pageWidgetUtils';
import { createDefaultWidgetData } from '../utils/widgetDefaults';

export interface PageWidget {
  type: WidgetType;
  data: any;
  index: number;
}

export const useDynamicPageWidgets = (pageType: string) => {
  const siteContent = useSelector(selectPlainSiteContent);
  const { isEditMode } = useAppSelector((state) => state.editMode);
  const { updateLoading, error } = useAppSelector((state) => state.siteContent);
  const dispatch = useAppDispatch();
  
  const [localWidgets, setLocalWidgets] = useState<PageWidget[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local widgets from Redux after any changes
  useEffect(() => {
    const pageWidgets = siteContent?.widgets?.filter((widget: Widget) => 
      isPageWidget(widget, pageType)
    ) || [];

    const mappedWidgets: PageWidget[] = pageWidgets.map((widget: Widget) => {
      const originalIndex = siteContent?.widgets?.findIndex((w: Widget) => w === widget) || 0;
      return {
        type: widget.type as WidgetType,
        data: widget.data,
        index: originalIndex
      };
    });

    setLocalWidgets(mappedWidgets);
  }, [siteContent, pageType]);

  // Reset saving state when loading completes
  useEffect(() => {
    if (!updateLoading && isSaving) {
      setIsSaving(false);
    }
  }, [updateLoading, isSaving]);

  // Update widget data locally
  const updateWidgetData = useCallback((widgetIndex: number, newData: any) => {
    setLocalWidgets(prev => 
      prev.map((widget, idx) => 
        idx === widgetIndex 
          ? { ...widget, data: newData }
          : widget
      )
    );
  }, []);

  // Save all widget data changes to Redux and backend
  const saveWidgets = useCallback(async () => {
    setIsSaving(true);
    
    // Save widget data updates to Redux (add/delete already in Redux)
    localWidgets.forEach((widget) => {
      dispatch(updateWidgetDataAction({ index: widget.index, data: widget.data }));
    });

    // Save to backend
    dispatch(updateSiteContentRequest());
  }, [dispatch, localWidgets]);

  // Toggle edit mode (save when closing edit mode)
  const toggleEditModeWithSave = useCallback(async () => {
    if (isEditMode) {
      await saveWidgets();
    }
    dispatch(toggleEditMode());
  }, [dispatch, isEditMode, saveWidgets]);

  // Add new widget to page (after specific index)
  const addWidget = useCallback((widgetType: WidgetType, widgetData: any, afterIndex?: number) => {
    // Generate widget data if not provided
    const finalWidgetData = widgetData || createDefaultWidgetData(widgetType);
    
    // Generate unique widget ID with page name
    // Format: WidgetType_PageName_UniqueID (e.g., "LeftContentRightMedia_events_A1B2C3D4")
    const uniqueId = generatePageWidgetId(widgetType, pageType);
    
    const newWidget = { 
      type: uniqueId,  // Unique type for easy findIndex
      id: uniqueId,    // Same as type for consistency
      data: finalWidgetData 
    };
    
    dispatch(addWidgetSection({ widget: newWidget, afterIndex }));
    // API call will happen when user clicks Save button
  }, [dispatch, pageType]);

  // Delete widget from page using stored index
  const deleteWidget = useCallback((widgetIndex: number) => {
    const widgetToDelete = localWidgets[widgetIndex];
    if (!widgetToDelete) return;
    
    // Use the stored global index for accurate deletion
    dispatch(deleteWidgetSection(widgetToDelete.index));
    // API call will happen when user clicks Save button
  }, [dispatch, localWidgets]);

  return {
    widgets: localWidgets,
    updateWidgetData,
    saveWidgets,
    toggleEditModeWithSave,
    addWidget,
    deleteWidget,
    isEditMode,
    isSaving,
    hasError: !!error,
    hasWidgets: localWidgets.length > 0
  };
};
